import React, { useId, useState } from "react";

import { PatientDobFilterFields, PatientDobFilterToggle } from "./patient_dob_filter";
import { patientMeta, patientSearchSummary, usePatientSearch } from "./patient_search";
import type { Patient } from "./patient_search";

interface FaxPatientAssignmentProps {
  searchUrl: string;
  assignUrl: string;
  authenticityToken: string;
  resourceName?: string;
  linkedPatients?: Patient[];
}

export default function FaxPatientAssignment({
  searchUrl,
  assignUrl,
  authenticityToken,
  resourceName = "fax",
  linkedPatients = [],
}: FaxPatientAssignmentProps) {
  const [dobFiltersVisible, setDobFiltersVisible] = useState(false);
  const dobFiltersId = useId();
  const { dobFilters, errorMessage, hasDobFilters, loading, query, results, setDobFilters, setQuery, trimmedQuery } = usePatientSearch(searchUrl);
  const resultSummary = patientSearchSummary({
    trimmedQuery,
    hasDobFilters,
    loading,
    errorMessage,
    resultsCount: results.length,
  });

  return (
    <div className="space-y-3">
      <p className="text-sm text-neutral-600 dark:text-neutral-300">
        {linkedPatients.length > 0
          ? `This ${resourceName} is linked to ${linkedPatients.length} patient${linkedPatients.length === 1 ? "" : "s"}. Search below to add another link or unlink an existing one.`
          : `Search for the correct patient and link the ${resourceName} without leaving this page.`}
      </p>

      {linkedPatients.length > 0 ? (
        <div className="space-y-2.5">
          {linkedPatients.map((patient) => (
            <div key={patient.id} className="rounded-lg border border-emerald-200/80 bg-emerald-50/70 p-3 dark:border-emerald-900/60 dark:bg-emerald-950/30">
              <div className="flex flex-wrap items-start justify-between gap-2.5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">Linked patient</p>
                  <p className="mt-0.5 text-sm font-semibold">
                    <a
                      href={patient.patientPath}
                      className="text-neutral-950 hover:text-emerald-700 dark:text-white dark:hover:text-emerald-300"
                    >
                      {patient.fullName}
                    </a>
                  </p>
                  {patientMeta(patient).length > 0 ? (
                    <p className="mt-1.5 text-sm text-neutral-600 dark:text-neutral-300">{patientMeta(patient).join(" | ")}</p>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <a href={patient.patientPath} className="app-button-secondary" target="_blank" rel="noreferrer">
                    Open chart
                  </a>
                  <form method="post" action={assignUrl}>
                    <input type="hidden" name="authenticity_token" value={authenticityToken} />
                    <input type="hidden" name="_method" value="patch" />
                    <input type="hidden" name="unlink_patient_id" value={patient.id} />
                    <button type="submit" className="app-button-secondary">
                      Unlink
                    </button>
                  </form>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div>
        <div className="flex items-center gap-0.5">
          <label htmlFor="patient-search" className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
            Find patient
          </label>
          <PatientDobFilterToggle
            controlsId={dobFiltersId}
            expanded={dobFiltersVisible}
            filters={dobFilters}
            onToggle={() => setDobFiltersVisible((visible) => !visible)}
          />
        </div>
        <input
          id="patient-search"
          type="search"
          value={query}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setQuery(event.target.value)}
          placeholder="Search name, phone, or email"
          autoComplete="off"
          className="app-input mt-2"
        />
        {dobFiltersVisible ? <PatientDobFilterFields filters={dobFilters} id={dobFiltersId} onChange={setDobFilters} /> : null}
        <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">{resultSummary}</p>
      </div>

      {results.length > 0 ? (
        <div className="space-y-2.5">
          {results.map((patient) => (
            <div
              key={patient.id}
              className="rounded-lg border border-neutral-200 bg-neutral-50/80 p-3 transition hover:border-emerald-200 hover:bg-white dark:border-neutral-800 dark:bg-neutral-950/60 dark:hover:border-emerald-900/50 dark:hover:bg-neutral-900"
            >
              <div className="flex flex-wrap items-start justify-between gap-2.5">
                <div>
                  <p className="text-sm font-semibold">
                    <a
                      href={patient.patientPath}
                      className="text-neutral-950 hover:text-emerald-700 dark:text-white dark:hover:text-emerald-300"
                    >
                      {patient.fullName}
                    </a>
                  </p>
                  {patientMeta(patient).length > 0 ? (
                    <p className="mt-0.5 text-sm text-neutral-600 dark:text-neutral-300">{patientMeta(patient).join(" | ")}</p>
                  ) : null}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <a href={patient.patientPath} className="app-button-secondary" target="_blank" rel="noreferrer">
                  Open chart
                </a>
                <form method="post" action={assignUrl}>
                  <input type="hidden" name="authenticity_token" value={authenticityToken} />
                  <input type="hidden" name="_method" value="patch" />
                  <input type="hidden" name="patient_id" value={patient.id} />
                  <button type="submit" className="app-button-primary">
                    {linkedPatients.some((linkedPatient) => String(linkedPatient.id) === String(patient.id)) ? "Already linked" : "Add link"}
                  </button>
                </form>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
